<script setup>
/**
 * 意图视频详情 —— 生成队列和资源列表共用这一个。
 *
 * 两个入口点开的是同一件事的两半：队列那条给 request-id，资源那条给 resource-id，
 * 服务端把另一半补齐（见 /intent-video-detail）。所以这里不认调用方传进来的行对象——
 * 两张表的行字段本来就不一样，靠行对象拼详情的结果就是两套详情各缺一半，
 * 「队列里看不到 embedding、资源里看不到排在第几步」那种缺口永远补不齐。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Eye, Pencil, Plus, RotateCcw, Upload, Video, X } from '@lucide/vue'
import { Badge, Button, ConfirmAction, FormField, Input, Modal, PageHeader, Select, Spinner, Textarea, useToast } from '@drama/ui'

import {
  disableIntentVideo,
  enableIntentVideo,
  getIntentVideoDetail,
  getIntentVideoTaskPreview,
  renderIntentVideo,
  replaceIntentVideo,
  updateIntentVideoSpec,
  updateIntentVideoText,
  writeIntentVideoPrompt,
} from '../../api/intent-videos.js'
import { formatBytes, formatDate, formatMs, queueStatusLabel, queueStatusVariant, statusLabel, statusVariant } from './format.js'

/** 和服务端 MAX_ASSET_UPLOAD_BYTES 同一个数。前端先挡一下，省得传完 25MB 才收到 400。 */
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

/** 出片跑在服务端后台，唯一的进度来源是队列那一行的状态，所以在这两个态上轮询。 */
const LIVE_STATUSES = ['prompting', 'rendering']
const POLL_MS = 5000

/** 参数表单摊成固定几格，不给一个 JSON 编辑框：JSON 里打错一个逗号，报错是「解析失败」而不是「steps 得 ≤ 8」。 */
const NUMBER_PARAMS = [
  { key: 'width', required: true },
  { key: 'height', required: true },
  { key: 'frames', required: true },
  { key: 'steps', required: true },
  { key: 'seed', required: true },
  { key: 'ref_image_short_edge', required: true },
]

/**
 * 宽高帧数步数种子只挑「H3 Console 实测过的那几档」，不给自由输入——H3 现网就是按
 * 这些固定组合放行的，输个没测过的数字大概率是白跑一次往返。选项照抄
 * http://192.168.110.67:8080/newtask.html（内网 H3 控制台）现有的下拉档位。
 */
const RESOLUTION_OPTIONS = [
  { value: '1344x768', label: '1344×768（官方默认分辨率）', width: 1344, height: 768 },
  { value: '832x480', label: '832×480（标准档）', width: 832, height: 480 },
  { value: '448x256', label: '448×256（长时长档）', width: 448, height: 256 },
  { value: '1024x576', label: '1024×576（高清档）', width: 1024, height: 576 },
]
const FRAMES_OPTIONS = [
  { value: '124', label: '5s' },
  { value: '243', label: '10s' },
  { value: '362', label: '15s' },
  { value: '412', label: '17s（1344×768 实测上限）' },
]
const STEPS_OPTIONS = [
  { value: '20', label: '20 步' },
  { value: '50', label: '50 步（实测全质量，约 9.6 倍耗时）' },
]
const SEED_OPTIONS = [
  { value: '0', label: '0' },
  { value: '42', label: '42' },
  { value: '123', label: '123' },
  { value: '2024', label: '2024' },
  { value: 'random', label: '随机' },
]

/**
 * H3 Console 里根本不让选这个值——它是接口那层写死的默认值，不是运营该操心的参数。
 * 后台这边也一样：不给下拉、不给输入框，写规格时直接摊平进去。
 */
const DEFAULT_REF_IMAGE_SHORT_EDGE = 768

const props = defineProps({
  requestId: { type: String, default: null },
  resourceId: { type: String, default: null },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()

/** 从哪个页面点进来的，就退回哪个页面——按路由名判断，不用额外传参。 */
const FROM_PAGE = {
  'intent-video-queue-detail': 'intent-video-queue',
  'intent-video-resource-detail': 'intent-video-resources',
  'intent-video-recall-detail': 'intent-video-recall',
}
function back() {
  router.push({ name: FROM_PAGE[route.name] ?? 'intent-video-queue' })
}

const loading = ref(false)
const error = ref('')
const data = ref(null)
let pollTimer = null

const editingIntent = ref(false)
const draftIntent = ref('')
const savingIntent = ref(false)
const togglingStatus = ref(false)

const editingSpec = ref(false)
const draftSpec = ref(null)
const specError = ref('')
const savingSpec = ref(false)
const rendering = ref(false)

const fileInput = ref(null)
const pendingFile = ref(null)
const replaceNote = ref('')
const uploading = ref(false)

/** 参考图放大预览：点缩略图弹层看原图原比例，不再靠新开标签页。 */
const previewImage = ref(null)
function openPreview(url, label) {
  previewImage.value = { url, label }
}

/**
 * 任务书预览：这条请求现在触发生成会发给 Lemonboss 什么内容，不调用 Lemonboss、不改状态、
 * 跟这条请求当前是什么状态无关——运营怀疑参考图配错了，点开就能看，不用找开发翻日志。
 */
const taskPreview = ref(null)
const taskPreviewOpen = ref(false)
const loadingPreview = ref(false)
async function openTaskPreview() {
  taskPreviewOpen.value = true
  if (taskPreview.value !== null) return
  loadingPreview.value = true
  try {
    taskPreview.value = await getIntentVideoTaskPreview(request.value.id)
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
    taskPreviewOpen.value = false
  } finally {
    loadingPreview.value = false
  }
}

const request = computed(() => data.value?.request ?? null)
const resource = computed(() => data.value?.resource ?? null)
const spec = computed(() => data.value?.spec ?? null)
const menu = computed(() => data.value?.reference_menu ?? [])
const live = computed(() => request.value !== null && LIVE_STATUSES.includes(request.value.status))
const disabled = computed(() => resource.value?.status === 'rejected')

/** 出片按钮只在「有规格、有队列位置能承载状态、当前不在跑」时才点得动。 */
const canRender = computed(() => spec.value !== null && resource.value !== null && !live.value)

/** 还没资源、还在 pending/prompting 的队列行才写得了第一版提示词；done/failed 走别的路径（重出片、retry）。 */
const canWritePrompt = computed(() => request.value !== null && ['pending', 'prompting'].includes(request.value.status))

const turboOptions = computed(() => [
  { value: 'true', label: t('visualGameAdmin.intentVideo.spec.turboOn') },
  { value: 'false', label: t('visualGameAdmin.intentVideo.spec.turboOff') },
])

/** 分辨率是宽高两个字段拼出来选的，Select 只能绑一个值，所以用 computed 在存取时拆装。 */
const draftResolution = computed({
  get: () => {
    const { width, height } = draftSpec.value.params
    return width && height ? `${width}x${height}` : ''
  },
  set: value => {
    const preset = RESOLUTION_OPTIONS.find(option => option.value === value)
    if (preset === undefined) return
    draftSpec.value.params.width = String(preset.width)
    draftSpec.value.params.height = String(preset.height)
  },
})

/** 选"随机"当场掷一个种子存进草稿，不把字符串 "random" 带去提交——后端要的是真整数。 */
const seedModel = computed({
  get: () => draftSpec.value.params.seed,
  set: value => {
    draftSpec.value.params.seed = value === 'random' ? String(Math.floor(Math.random() * 1_000_000)) : value
  },
})

const unusedReferences = computed(() => {
  const used = new Set(draftSpec.value?.references ?? [])
  return menu.value.filter(reference => !used.has(reference.id))
})

watch(() => [props.requestId, props.resourceId], () => {
  stopPolling()
  editingIntent.value = false
  editingSpec.value = false
  specError.value = ''
  clearPendingFile()
  data.value = null
  load()
}, { immediate: true })

onBeforeUnmount(stopPolling)

async function load() {
  if (props.requestId === null && props.resourceId === null) return
  loading.value = data.value === null
  error.value = ''
  try {
    data.value = await getIntentVideoDetail({ requestId: props.requestId, resourceId: props.resourceId })
    schedulePoll()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

/**
 * 出片要跑 152~657 秒，接口早就返回了，状态得自己回来看。
 * 轮询只在 prompting / rendering 期间开，出了终态就停——常开会让一个开着的弹窗
 * 每 5 秒打一次接口，一直打到运营下班。
 */
function schedulePoll() {
  stopPolling()
  if (!live.value) return
  pollTimer = setTimeout(load, POLL_MS)
}

function stopPolling() {
  if (pollTimer !== null) clearTimeout(pollTimer)
  pollTimer = null
}

// ------------------------------------------------------------------ 意图文本

function startEditIntent() {
  draftIntent.value = resource.value.intent
  editingIntent.value = true
}

async function saveIntent() {
  savingIntent.value = true
  try {
    await updateIntentVideoText(resource.value.id, draftIntent.value.trim())
    toast.success(t('visualGameAdmin.intentVideo.detail.intentSaved'))
    editingIntent.value = false
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    savingIntent.value = false
  }
}

// ------------------------------------------------------------------ 出片规格

/**
 * 数字一律以字符串形态放在草稿里：Input 无论 type 是什么都只发字符串，
 * 存成数字的话清空输入框会得到 NaN，然后带着 NaN 提交。提交那一刻统一转。
 */
function startEditSpec() {
  const params = spec.value.params
  const draftParams = {}
  for (const field of NUMBER_PARAMS) {
    draftParams[field.key] = params[field.key] === undefined ? '' : String(params[field.key])
  }
  // 写死的接口默认值，不读旧规格里可能缺失/过期的那份——见 DEFAULT_REF_IMAGE_SHORT_EDGE 注释。
  draftParams.ref_image_short_edge = String(DEFAULT_REF_IMAGE_SHORT_EDGE)
  draftSpec.value = {
    prompt: spec.value.prompt,
    references: [...spec.value.references],
    params: draftParams,
    turbo: params.turbo === true ? 'true' : 'false',
    rationale: spec.value.rationale ?? '',
  }
  specError.value = ''
  editingSpec.value = true
}

/** 从零开始写第一版规格：空白表单，没有旧值可抄。 */
function startCreateSpec() {
  const draftParams = {}
  for (const field of NUMBER_PARAMS) draftParams[field.key] = ''
  draftParams.ref_image_short_edge = String(DEFAULT_REF_IMAGE_SHORT_EDGE)
  draftSpec.value = {
    prompt: '',
    references: [],
    params: draftParams,
    turbo: 'true',
    rationale: '',
  }
  specError.value = ''
  editingSpec.value = true
}

function moveReference(index, delta) {
  const list = draftSpec.value.references
  const target = index + delta
  if (target < 0 || target >= list.length) return
  ;[list[index], list[target]] = [list[target], list[index]]
}

function removeReference(index) {
  draftSpec.value.references.splice(index, 1)
}

function addReference(id) {
  draftSpec.value.references.push(id)
}

function referenceOf(id) {
  return menu.value.find(reference => reference.id === id) ?? null
}

async function saveSpec() {
  savingSpec.value = true
  specError.value = ''
  try {
    const params = { turbo: draftSpec.value.turbo === 'true' }
    for (const field of NUMBER_PARAMS) {
      const raw = String(draftSpec.value.params[field.key]).trim()
      // 选填项留空就是不传，不是传 0：ref_image_short_edge 传 0 会让 H3 把参考图缩到没有。
      if (raw === '' && !field.required) continue
      params[field.key] = raw === '' ? null : Number(raw)
    }
    const payload = {
      prompt: draftSpec.value.prompt,
      references: draftSpec.value.references,
      params,
      rationale: draftSpec.value.rationale.trim() || null,
    }
    if (resource.value === null) {
      // 第一次写：这条请求还没有资源，建资源、转 prompt_ready 都在服务端一次做完，
      // 完事之后 resource/renders 这些字段全是新的，重拉一遍详情比自己拼省事也不容易漏。
      await writeIntentVideoPrompt(request.value.id, payload)
      toast.success(t('visualGameAdmin.intentVideo.spec.saved'))
      editingSpec.value = false
      await load()
    } else {
      const result = await updateIntentVideoSpec(resource.value.id, payload)
      data.value = { ...data.value, spec: result.spec }
      toast.success(t('visualGameAdmin.intentVideo.spec.saved'))
      editingSpec.value = false
    }
  } catch (requestError) {
    // 规格报错是「frames 必须是 17n+5，最近的合法值 124」这种照做就能改对的话，
    // 留在表单里，不塞进一个几秒后就消失的 toast。
    specError.value = requestError.message
  } finally {
    savingSpec.value = false
  }
}

async function render() {
  rendering.value = true
  try {
    await renderIntentVideo(resource.value.id)
    toast.success(t('visualGameAdmin.intentVideo.spec.renderStarted'))
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    rendering.value = false
  }
}

// ------------------------------------------------------------------ 换片

function clearPendingFile() {
  pendingFile.value = null
  replaceNote.value = ''
  if (fileInput.value) fileInput.value.value = ''
}

function onFilePicked(event) {
  const file = event.target.files[0]
  if (file === undefined) return
  if (file.type !== 'video/mp4') {
    toast.error(t('visualGameAdmin.intentVideo.detail.replaceWrongType', { mime: file.type || '?' }))
    clearPendingFile()
    return
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    toast.error(t('visualGameAdmin.intentVideo.detail.replaceTooLarge', { size: formatBytes(file.size) }))
    clearPendingFile()
    return
  }
  pendingFile.value = file
}

/** FileReader 而不是 btoa(String.fromCharCode(...bytes))：几 MB 的片子会把参数栈打爆。 */
function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error(`读取文件失败: ${file.name}`))
    reader.onload = () => resolve(String(reader.result).split(',')[1])
    reader.readAsDataURL(file)
  })
}

async function submitReplace() {
  uploading.value = true
  try {
    const file = pendingFile.value
    await replaceIntentVideo(resource.value.id, {
      dataBase64: await readAsBase64(file),
      fileName: file.name,
      mime: file.type,
      note: replaceNote.value.trim() || null,
    })
    toast.success(t('visualGameAdmin.intentVideo.detail.replaced'))
    clearPendingFile()
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    uploading.value = false
  }
}

// ------------------------------------------------------------------ 禁用 / 恢复

function onDisabled() {
  toast.success(t('visualGameAdmin.intentVideo.detail.disabled'))
  load()
}

async function enable() {
  togglingStatus.value = true
  try {
    await enableIntentVideo(resource.value.id)
    toast.success(t('visualGameAdmin.intentVideo.detail.enabled'))
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    togglingStatus.value = false
  }
}
</script>

<template>
  <div class="ivd-page vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/IntentVideoDetail.vue#detail">
    <PageHeader :title="t('visualGameAdmin.intentVideo.detail.title')">
      <template #actions>
        <Button variant="ghost" @click="back">
          <ArrowLeft :size="16" /> {{ t('visualGameAdmin.intentVideo.detail.back') }}
        </Button>
      </template>
    </PageHeader>

    <div v-if="loading && !data" class="ivd__state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="ivd__state">
      <p class="ivd__error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <div v-else-if="data" class="ivd">
      <!-- 两个状态分开摆：资源状态说的是「玩家能不能召回它」，队列状态说的是「出片走到哪一步」。 -->
      <section class="ivd__head">
        <p class="ivd__intent">{{ resource?.intent ?? request?.intent_text }}</p>
        <div class="ivd__badges">
          <Badge v-if="resource" :variant="statusVariant(resource.status)" size="sm">
            {{ statusLabel(t, resource.status) }}
          </Badge>
          <Badge v-if="request" :variant="queueStatusVariant(request.status)" size="sm">
            {{ queueStatusLabel(t, request.status) }}
          </Badge>
          <span v-if="request" class="ivd__meta">
            {{ t('visualGameAdmin.intentVideo.detail.occurrenceMeta', {
              n: request.occurrences,
              source: t(`visualGameAdmin.intentVideo.source.${request.source}`),
              attempt: request.attempt,
            }) }}
          </span>
          <span v-if="resource?.hit_count !== undefined" class="ivd__meta">
            {{ t('visualGameAdmin.intentVideo.detail.hitMeta', { n: resource.hit_count }) }}
          </span>
          <Button v-if="request" variant="secondary" size="sm" @click="openTaskPreview">
            <Eye :size="14" /> {{ t('visualGameAdmin.intentVideo.detail.previewTask') }}
          </Button>
        </div>
        <p v-if="request?.last_error" class="ivd__error">{{ request.last_error }}</p>
        <p v-if="live" class="ivd__live">
          <Spinner /> {{ t('visualGameAdmin.intentVideo.spec.renderingNote') }}
        </p>
      </section>

      <div class="ivd__video-wrap">
        <video
          v-if="resource?.video_url"
          :key="resource.video_url"
          :src="resource.video_url"
          controls
          playsinline
          preload="metadata"
          class="ivd__video"
        />
        <div v-else class="ivd__no-video">{{ t('visualGameAdmin.intentVideo.detail.noVideo') }}</div>
        <div v-if="resource" class="ivd__video-meta">
          <span v-if="resource.video_width">{{ resource.video_width }}×{{ resource.video_height }}</span>
          <span v-if="resource.video_duration_ms">{{ formatMs(resource.video_duration_ms) }}</span>
          <span v-if="resource.video_size">{{ formatBytes(resource.video_size) }}</span>
          <span v-if="resource.has_audio !== null && resource.has_audio !== undefined">
            {{ resource.has_audio ? t('visualGameAdmin.intentVideo.detail.hasAudio') : t('visualGameAdmin.intentVideo.detail.noAudio') }}
          </span>
        </div>
        <!-- 换过片的资源，现在播的这条不是出片记录里的最后一条。这句话得挨着播放器，
             不能藏在下面的换片卡片里——看画面的人第一眼要知道它是哪来的。 -->
        <p v-if="data.manual_uploads.length" class="ivd__warn">
          {{ t('visualGameAdmin.intentVideo.spec.manualNote', {
            n: data.manual_uploads.length,
            at: formatDate(data.manual_uploads[data.manual_uploads.length - 1].at),
          }) }}
        </p>
      </div>

      <!-- 出片规格：这一屏是「下一次按什么拍」，改完点重新出片。resource 还没建出来时
           （pending/prompting）也要露出来，不然「写提示词」的入口根本看不见。 -->
      <section v-if="resource || canWritePrompt" class="ivd__card">
        <h4>
          {{ t('visualGameAdmin.intentVideo.spec.title') }}
          <span class="ivd__card-hint">{{ t('visualGameAdmin.intentVideo.spec.titleHint') }}</span>
        </h4>
        <div class="ivd__body">
          <div v-if="spec === null && !editingSpec" class="ivd__inline-actions">
            <p class="ivd__hint">{{ t('visualGameAdmin.intentVideo.spec.empty') }}</p>
            <Button v-if="canWritePrompt" variant="primary" size="sm" @click="startCreateSpec">
              <Pencil :size="14" /> {{ t('visualGameAdmin.intentVideo.spec.write') }}
            </Button>
          </div>

          <template v-if="editingSpec">
            <FormField :label="t('visualGameAdmin.intentVideo.detail.prompt')" required>
              <Textarea v-model="draftSpec.prompt" :rows="10" :maxlength="20000" />
            </FormField>

            <div class="ivd__refs-edit">
              <span class="ivd__sub">{{ t('visualGameAdmin.intentVideo.spec.references') }}</span>
              <p class="ivd__hint">{{ t('visualGameAdmin.intentVideo.spec.referencesHint') }}</p>
              <div v-for="(id, index) in draftSpec.references" :key="id" class="ivd__ref-row">
                <span class="ivd__ref-tag">&lt;Picture {{ index + 1 }}&gt;</span>
                <img v-if="referenceOf(id)" :src="referenceOf(id).url" :alt="id" />
                <div class="ivd__ref-text">
                  <strong>{{ id }}</strong>
                  <span>{{ referenceOf(id)?.role }}</span>
                  <span v-if="referenceOf(id)?.note" class="ivd__ref-note">{{ referenceOf(id).note }}</span>
                </div>
                <div class="ivd__ref-ops">
                  <Button variant="ghost" size="sm" :disabled="index === 0" @click="moveReference(index, -1)">
                    <ArrowUp :size="14" />
                  </Button>
                  <Button variant="ghost" size="sm" :disabled="index === draftSpec.references.length - 1" @click="moveReference(index, 1)">
                    <ArrowDown :size="14" />
                  </Button>
                  <Button variant="ghost" size="sm" @click="removeReference(index)">
                    <X :size="14" />
                  </Button>
                </div>
              </div>
              <div v-if="unusedReferences.length" class="ivd__inline-actions">
                <Button
                  v-for="reference in unusedReferences"
                  :key="reference.id"
                  variant="secondary"
                  size="sm"
                  @click="addReference(reference.id)"
                >
                  <Plus :size="14" /> {{ reference.id }}
                </Button>
              </div>
            </div>

            <div class="ivd__params-grid">
              <FormField :label="t('visualGameAdmin.intentVideo.spec.param.resolution')" required>
                <Select v-model="draftResolution" :options="RESOLUTION_OPTIONS" size="sm" block />
              </FormField>
              <FormField :label="t('visualGameAdmin.intentVideo.spec.param.frames')" required>
                <Select v-model="draftSpec.params.frames" :options="FRAMES_OPTIONS" size="sm" block />
              </FormField>
              <FormField :label="t('visualGameAdmin.intentVideo.spec.param.steps')" required>
                <Select v-model="draftSpec.params.steps" :options="STEPS_OPTIONS" size="sm" block />
              </FormField>
              <FormField :label="t('visualGameAdmin.intentVideo.spec.param.turbo')" required>
                <Select v-model="draftSpec.turbo" :options="turboOptions" size="sm" block />
              </FormField>
              <FormField :label="t('visualGameAdmin.intentVideo.spec.param.seed')" required>
                <Select v-model="seedModel" :options="SEED_OPTIONS" size="sm" block />
              </FormField>
            </div>
            <p class="ivd__hint">{{ t('visualGameAdmin.intentVideo.spec.protocolRules') }}</p>

            <FormField :label="t('visualGameAdmin.intentVideo.spec.rationale')" :hint="t('visualGameAdmin.intentVideo.spec.rationaleHint')">
              <Input v-model="draftSpec.rationale" size="sm" :maxlength="1000" />
            </FormField>

            <!-- 选档位的依据用实测数，不写一张推荐表：同一份提示词 4 步 152 秒、20 步 657 秒，
                 值不值这个价钱只有看得见出现次数的人能判。 -->
            <div v-if="data.measured_cost.length" class="ivd__cost">
              <span class="ivd__sub">{{ t('visualGameAdmin.intentVideo.spec.measuredCost') }}</span>
              <div v-for="entry in data.measured_cost" :key="entry.档位" class="ivd__cost-row">
                <code>{{ entry.档位 }}</code>
                <strong>{{ entry.出片秒 }}s</strong>
                <span>{{ entry.说明 }}</span>
              </div>
            </div>

            <p v-if="specError" class="ivd__error">{{ specError }}</p>
            <div class="ivd__inline-actions">
              <Button
                variant="primary"
                size="sm"
                :loading="savingSpec"
                :disabled="draftSpec.prompt.trim() === '' || draftSpec.references.length === 0"
                @click="saveSpec"
              >
                {{ t('visualGameAdmin.intentVideo.spec.save') }}
              </Button>
              <Button variant="ghost" size="sm" :disabled="savingSpec" @click="editingSpec = false">
                {{ t('visualGameAdmin.common.cancel') }}
              </Button>
            </div>
          </template>

          <template v-else-if="spec !== null">
            <pre class="ivd__pre">{{ spec.prompt }}</pre>
            <div class="ivd__refs">
              <div v-for="(id, index) in spec.references" :key="id" class="ivd__ref">
                <button
                  v-if="referenceOf(id)"
                  type="button"
                  class="ivd__ref-zoom"
                  @click="openPreview(referenceOf(id).url, `<Picture ${index + 1}> · ${id}`)"
                >
                  <img :src="referenceOf(id).url" :alt="id" />
                </button>
                <div class="ivd__ref-info">
                  <span class="ivd__ref-slot">&lt;Picture {{ index + 1 }}&gt; · {{ id }}</span>
                  <span class="ivd__ref-role">{{ referenceOf(id)?.role }}</span>
                  <span v-if="referenceOf(id)?.note" class="ivd__ref-note">{{ referenceOf(id).note }}</span>
                </div>
              </div>
            </div>
            <div class="ivd__params-view">
              <span>{{ spec.params.width }}×{{ spec.params.height }}×{{ spec.params.frames }}f</span>
              <span>steps={{ spec.params.steps }}</span>
              <span>turbo={{ spec.params.turbo }}</span>
              <span>seed={{ spec.params.seed }}</span>
              <span v-if="spec.params.ref_image_short_edge">ref_short_edge={{ spec.params.ref_image_short_edge }}</span>
            </div>
            <p v-if="spec.rationale" class="ivd__hint">{{ t('visualGameAdmin.intentVideo.spec.rationale') }}：{{ spec.rationale }}</p>
            <div class="ivd__inline-actions">
              <Button variant="secondary" size="sm" :disabled="live" @click="startEditSpec">
                <Pencil :size="14" /> {{ t('visualGameAdmin.intentVideo.spec.edit') }}
              </Button>
              <Button variant="primary" size="sm" :loading="rendering" :disabled="!canRender" @click="render">
                <Video :size="14" /> {{ t('visualGameAdmin.intentVideo.spec.render') }}
              </Button>
              <span class="ivd__hint">{{ t('visualGameAdmin.intentVideo.spec.renderHint') }}</span>
            </div>
          </template>
        </div>
      </section>

      <!-- 出片记录：改规格不动它，所以「现在播的这条是按什么拍的」永远答得出。 -->
      <section v-if="data.renders.length" class="ivd__card">
        <h4>{{ t('visualGameAdmin.intentVideo.spec.renders') }}</h4>
        <div class="ivd__body">
          <div v-for="entry in [...data.renders].reverse()" :key="entry.seq" class="ivd__render">
            <div class="ivd__render-head">
              <strong>#{{ entry.seq }}</strong>
              <span>{{ formatDate(entry.at) }}</span>
              <code>{{ entry.h3_job_id }}</code>
              <span v-if="entry.media">{{ entry.media.width }}×{{ entry.media.height }} · {{ formatMs(entry.media.duration_ms) }}</span>
            </div>
            <div class="ivd__render-params">
              {{ entry.params.width }}×{{ entry.params.height }}×{{ entry.params.frames }}f
              steps={{ entry.params.steps }} turbo={{ entry.params.turbo }} seed={{ entry.params.seed }}
              · {{ t('visualGameAdmin.intentVideo.spec.renderedBy', { by: entry.by }) }}
            </div>
          </div>
        </div>
      </section>

      <section v-if="resource" class="ivd__card">
        <h4>{{ t('visualGameAdmin.intentVideo.detail.replace') }}</h4>
        <div class="ivd__body">
          <p class="ivd__hint">{{ t('visualGameAdmin.intentVideo.detail.replaceHint') }}</p>
          <input
            ref="fileInput"
            type="file"
            accept="video/mp4"
            class="ivd__file"
            @change="onFilePicked"
          />
          <div v-if="pendingFile === null" class="ivd__inline-actions">
            <Button size="sm" variant="secondary" @click="fileInput.click()">
              <Upload :size="14" /> {{ t('visualGameAdmin.intentVideo.detail.replacePick') }}
            </Button>
          </div>
          <template v-else>
            <div class="ivd__kv">
              <span class="ivd__k">{{ t('visualGameAdmin.intentVideo.detail.replaceFile') }}</span>
              <span class="ivd__v ivd__v--mono">{{ pendingFile.name }} · {{ formatBytes(pendingFile.size) }}</span>
            </div>
            <FormField :label="t('visualGameAdmin.intentVideo.detail.replaceNote')">
              <Input
                v-model="replaceNote"
                size="sm"
                :maxlength="500"
                :placeholder="t('visualGameAdmin.intentVideo.detail.replaceNotePlaceholder')"
              />
            </FormField>
            <div class="ivd__inline-actions">
              <Button size="sm" variant="primary" :loading="uploading" @click="submitReplace">
                <Upload :size="14" /> {{ t('visualGameAdmin.intentVideo.detail.replaceSubmit') }}
              </Button>
              <Button size="sm" variant="ghost" :disabled="uploading" @click="clearPendingFile">
                {{ t('visualGameAdmin.common.cancel') }}
              </Button>
            </div>
          </template>
        </div>
      </section>

      <section v-if="resource" class="ivd__card">
        <h4>{{ t('visualGameAdmin.intentVideo.detail.match') }}</h4>
        <div class="ivd__body">
          <template v-if="editingIntent">
            <FormField :label="t('visualGameAdmin.intentVideo.detail.intent')" required>
              <Textarea v-model="draftIntent" :maxlength="2000" :rows="3" />
            </FormField>
            <p class="ivd__hint">{{ t('visualGameAdmin.intentVideo.detail.intentHint') }}</p>
            <div class="ivd__inline-actions">
              <Button size="sm" variant="primary" :loading="savingIntent" :disabled="draftIntent.trim() === ''" @click="saveIntent">
                {{ t('visualGameAdmin.common.save') }}
              </Button>
              <Button size="sm" variant="ghost" :disabled="savingIntent" @click="editingIntent = false">
                {{ t('visualGameAdmin.common.cancel') }}
              </Button>
            </div>
          </template>
          <template v-else>
            <div class="ivd__kv">
              <span class="ivd__k">{{ t('visualGameAdmin.intentVideo.detail.intent') }}</span>
              <span class="ivd__v ivd__v--hl">{{ resource.intent }}</span>
            </div>
            <div v-if="resource.rejection_reason" class="ivd__kv">
              <span class="ivd__k">{{ t('visualGameAdmin.intentVideo.detail.rejectionReason') }}</span>
              <span class="ivd__v">{{ resource.rejection_reason }}</span>
            </div>
            <div v-if="resource.embedding" class="ivd__chain">
              <div class="ivd__step">
                <span class="ivd__step-label">{{ t('visualGameAdmin.intentVideo.detail.input') }}</span>
                <code>{{ resource.intent }}</code>
              </div>
              <span class="ivd__arrow">→</span>
              <div class="ivd__step">
                <span class="ivd__step-label">{{ resource.embedding.provider }} · {{ resource.embedding.model }}</span>
                <code>{{ formatMs(resource.embedding.duration_ms) }} · {{ resource.embedding.tokens ?? '?' }} tokens</code>
              </div>
              <span class="ivd__arrow">→</span>
              <div class="ivd__step">
                <span class="ivd__step-label">{{ t('visualGameAdmin.intentVideo.detail.output') }}</span>
                <code>{{ t('visualGameAdmin.intentVideo.detail.vector') }}</code>
              </div>
            </div>
            <div v-if="resource.embedding" class="ivd__kv ivd__kv--sm">
              <span class="ivd__k">call_id</span><code class="ivd__v ivd__v--mono">{{ resource.embedding.call_id }}</code>
            </div>
          </template>
        </div>
      </section>

      <section class="ivd__card">
        <h4>{{ t('visualGameAdmin.intentVideo.detail.origin') }}</h4>
        <div class="ivd__body">
          <div v-if="request" class="ivd__kv"><span class="ivd__k">request_id</span><code class="ivd__v ivd__v--mono">{{ request.id }}</code></div>
          <div v-if="request" class="ivd__kv">
            <span class="ivd__k">{{ t('visualGameAdmin.intentVideo.table.source') }}</span>
            <span class="ivd__v">{{ t(`visualGameAdmin.intentVideo.source.${request.source}`) }} · {{ request.source_ref || '-' }}</span>
          </div>
          <div v-if="resource" class="ivd__kv"><span class="ivd__k">resource_id</span><code class="ivd__v ivd__v--mono">{{ resource.id }}</code></div>
          <div v-if="resource" class="ivd__kv"><span class="ivd__k">created_by</span><code class="ivd__v ivd__v--mono">{{ resource.created_by }}</code></div>
          <div v-if="resource?.generation" class="ivd__kv ivd__kv--sm">
            <span class="ivd__k">H3 job</span><code class="ivd__v ivd__v--mono">{{ resource.generation.h3_job_id }}</code>
          </div>
          <div v-if="resource?.video_url" class="ivd__kv">
            <span class="ivd__k">{{ t('visualGameAdmin.intentVideo.detail.videoUrl') }}</span>
            <a class="ivd__v ivd__link" :href="resource.video_url" target="_blank" rel="noreferrer">
              <ExternalLink :size="12" /> {{ resource.video_url }}
            </a>
          </div>
          <div v-if="resource" class="ivd__kv">
            <span class="ivd__k">{{ t('visualGameAdmin.intentVideo.table.createdAt') }}</span>
            <span class="ivd__v">{{ formatDate(resource.created_at) }}</span>
          </div>
        </div>
      </section>

      <div class="ivd__footer">
        <Button v-if="resource && !editingIntent" variant="secondary" size="sm" @click="startEditIntent">
          <Pencil :size="14" /> {{ t('visualGameAdmin.intentVideo.detail.editIntent') }}
        </Button>
        <!-- ConfirmAction 渲染自己的触发按钮，没有默认插槽：文案走 label，动作走 action。 -->
        <ConfirmAction
          v-if="resource && !disabled"
          :label="t('visualGameAdmin.intentVideo.detail.disable')"
          :confirm-text="t('visualGameAdmin.intentVideo.detail.confirmDisable')"
          :cancel-label="t('visualGameAdmin.common.cancel')"
          :ok-label="t('visualGameAdmin.common.confirm')"
          :pending-label="t('visualGameAdmin.common.loading')"
          danger
          :action="() => disableIntentVideo(resource.id)"
          @done="onDisabled"
          @error="requestError => toast.error(requestError.message, { duration: 0 })"
        />
        <Button v-else-if="resource" variant="secondary" size="sm" :loading="togglingStatus" @click="enable">
          <RotateCcw :size="14" /> {{ t('visualGameAdmin.intentVideo.detail.enable') }}
        </Button>
      </div>
    </div>

    <Modal
      :open="previewImage !== null"
      :title="previewImage?.label ?? ''"
      width="min(1100px, 92vw)"
      @update:open="value => { if (!value) previewImage = null }"
    >
      <img v-if="previewImage" :src="previewImage.url" :alt="previewImage.label" class="ivd__preview-image" />
    </Modal>

    <Modal
      :open="taskPreviewOpen"
      :title="t('visualGameAdmin.intentVideo.detail.previewTask')"
      width="min(900px, 92vw)"
      @update:open="value => { taskPreviewOpen = value }"
    >
      <Spinner v-if="loadingPreview" />
      <template v-else-if="taskPreview">
        <p class="ivd__preview-hint">{{ t('visualGameAdmin.intentVideo.detail.previewTaskHint') }}</p>
        <div class="ivd__refs">
          <div v-for="reference in taskPreview.context.references" :key="reference.id" class="ivd__ref">
            <img :src="reference.url" :alt="reference.id" />
            <div class="ivd__ref-info">
              <span class="ivd__ref-slot">id={{ reference.id }}</span>
              <span class="ivd__ref-role">{{ reference.role }}</span>
            </div>
          </div>
        </div>
        <details class="ivd__preview-raw">
          <summary>{{ t('visualGameAdmin.intentVideo.detail.previewTaskRaw') }}</summary>
          <pre class="ivd__pre">{{ taskPreview.task }}</pre>
        </details>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.ivd { display: grid; gap: var(--drama-space-lg); }
.ivd__footer { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); padding-top: var(--drama-space-sm); border-top: 1px solid var(--drama-color-border); }
.ivd__state { display: grid; gap: var(--drama-space-md); justify-items: center; padding: var(--drama-space-xl) 0; }
.ivd__head { display: grid; gap: var(--drama-space-sm); }
.ivd__intent { margin: 0; font-size: var(--drama-font-size-lg); font-weight: 600; line-height: 1.5; }
.ivd__badges { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); }
.ivd__meta { font-size: 12px; color: var(--drama-color-text-muted); }
.ivd__live { display: flex; align-items: center; gap: var(--drama-space-sm); margin: 0; font-size: 12px; color: var(--drama-color-primary); }
.ivd__video-wrap { display: grid; gap: var(--drama-space-sm); }
.ivd__video { width: 100%; max-height: 360px; border-radius: var(--drama-radius-md); background: #000; }
.ivd__no-video {
  height: 180px; display: grid; place-items: center; border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt); color: var(--drama-color-text-muted);
}
.ivd__video-meta {
  display: flex; flex-wrap: wrap; gap: var(--drama-space-md);
  color: var(--drama-color-text-muted); font-family: var(--drama-font-family-mono, monospace); font-size: 12px;
}
.ivd__card { border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); overflow: hidden; }
.ivd__card h4 {
  display: flex; align-items: baseline; gap: var(--drama-space-sm);
  margin: 0; padding: var(--drama-space-sm) var(--drama-space-md);
  background: var(--drama-color-surface-alt); font-size: 12px; font-weight: 600;
  letter-spacing: 0.03em; color: var(--drama-color-text-muted); text-transform: uppercase;
}
.ivd__card-hint { font-size: 11px; font-weight: 400; letter-spacing: 0; text-transform: none; }
.ivd__body { padding: var(--drama-space-md); display: grid; gap: var(--drama-space-md); }
.ivd__sub { font-size: 12px; font-weight: 600; }
.ivd__hint { margin: 0; font-size: 12px; color: var(--drama-color-text-muted); }
.ivd__warn { margin: 0; font-size: 12px; color: var(--drama-color-warning, #8a5300); }
.ivd__error { margin: 0; font-size: 12px; color: var(--drama-color-danger); word-break: break-word; }
.ivd__file { display: none; }
.ivd__inline-actions { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); }
.ivd__kv { display: flex; align-items: baseline; gap: var(--drama-space-sm); }
.ivd__kv--sm { font-size: 11px; }
.ivd__k { flex-shrink: 0; min-width: 88px; color: var(--drama-color-text-muted); font-size: 12px; }
.ivd__v { font-size: 12px; color: var(--drama-color-text); word-break: break-all; line-height: 1.5; }
.ivd__v--hl { font-weight: 600; }
.ivd__v--mono { font-family: var(--drama-font-family-mono, monospace); font-size: 11px; background: none; padding: 0; }
.ivd__link { display: inline-flex; align-items: center; gap: 4px; color: var(--drama-color-primary); text-decoration: none; }
.ivd__link:hover { text-decoration: underline; }
.ivd__chain {
  display: flex; align-items: center; gap: var(--drama-space-sm); overflow-x: auto;
  padding: var(--drama-space-sm); background: var(--drama-color-bg); border-radius: var(--drama-radius-sm);
}
.ivd__step {
  display: grid; gap: 2px; flex-shrink: 0; padding: var(--drama-space-xs) var(--drama-space-sm);
  border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface);
}
.ivd__step-label { font-size: 11px; color: var(--drama-color-text-muted); }
.ivd__step code { font-family: var(--drama-font-family-mono, monospace); font-size: 11px; background: none; padding: 0; }
.ivd__arrow { color: var(--drama-color-text-muted); flex-shrink: 0; }
.ivd__pre {
  margin: 0; padding: var(--drama-space-md); background: var(--drama-color-bg);
  border-radius: var(--drama-radius-sm); font-family: var(--drama-font-family-mono, monospace);
  font-size: 12px; line-height: 1.7; white-space: pre-wrap; word-break: break-word;
  max-height: 400px; overflow-y: auto;
}
.ivd__refs { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--drama-space-md); }
.ivd__ref { border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); overflow: hidden; }
/* 参考图有正方形合图（角色设定表）、竖长人像等各种原始比例，裁成 16:9 会藏掉判断
   角色/服装是否选对所需要看的那部分。用 contain 完整显示缩略图，点击弹层看放大原图。 */
.ivd__ref-zoom { display: block; width: 100%; padding: 0; border: none; background: none; cursor: zoom-in; }
.ivd__ref img { width: 100%; max-height: 320px; object-fit: contain; background: var(--drama-color-surface-alt); display: block; }
.ivd__preview-image { display: block; width: 100%; max-height: 80vh; object-fit: contain; }
.ivd__preview-hint { margin: 0 0 var(--drama-space-md); color: var(--drama-color-text-muted); font-size: 13px; }
.ivd__preview-raw { margin-top: var(--drama-space-lg); }
.ivd__preview-raw summary { cursor: pointer; color: var(--drama-color-text-muted); font-size: 13px; }
.ivd__preview-raw .ivd__pre { margin-top: var(--drama-space-sm); }
.ivd__ref-info { padding: var(--drama-space-xs) var(--drama-space-sm); display: grid; gap: 2px; }
.ivd__ref-slot { font-size: 11px; font-weight: 600; color: var(--drama-color-primary); }
.ivd__ref-role { font-size: 11px; color: var(--drama-color-text-muted); }
.ivd__ref-note { font-size: 11px; color: var(--drama-color-warning, #8a5300); }
.ivd__refs-edit { display: grid; gap: var(--drama-space-sm); }
.ivd__ref-row {
  display: flex; align-items: center; gap: var(--drama-space-sm);
  padding: var(--drama-space-sm); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm);
}
.ivd__ref-row img { width: 72px; height: 44px; object-fit: cover; border-radius: var(--drama-radius-sm); flex-shrink: 0; }
.ivd__ref-tag {
  flex-shrink: 0; min-width: 84px; font-family: var(--drama-font-family-mono, monospace);
  font-size: 11px; color: var(--drama-color-primary);
}
.ivd__ref-text { display: grid; gap: 2px; min-width: 0; flex: 1; font-size: 11px; }
.ivd__ref-text span { color: var(--drama-color-text-muted); }
.ivd__ref-ops { display: flex; gap: 2px; flex-shrink: 0; }
.ivd__params-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: var(--drama-space-md); }
.ivd__params-view {
  display: flex; flex-wrap: wrap; gap: var(--drama-space-md);
  font-family: var(--drama-font-family-mono, monospace); font-size: 12px; color: var(--drama-color-text-muted);
}
.ivd__cost { display: grid; gap: var(--drama-space-xs); }
.ivd__cost-row {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: var(--drama-space-sm);
  font-size: 11px; color: var(--drama-color-text-muted);
}
.ivd__cost-row code { font-family: var(--drama-font-family-mono, monospace); }
.ivd__cost-row strong { color: var(--drama-color-text); }
.ivd__render { display: grid; gap: 2px; padding: var(--drama-space-sm) 0; border-top: 1px solid var(--drama-color-border); }
.ivd__render:first-child { border-top: 0; padding-top: 0; }
.ivd__render-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: var(--drama-space-sm); font-size: 12px; }
.ivd__render-head code { font-family: var(--drama-font-family-mono, monospace); font-size: 11px; }
.ivd__render-head span { color: var(--drama-color-text-muted); }
.ivd__render-params { font-family: var(--drama-font-family-mono, monospace); font-size: 11px; color: var(--drama-color-text-muted); }
</style>
